import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import { resetPassword } from "@/api/auth";

function Field({ children }) {
  return <div className="relative flex items-center">{children}</div>
}

export default function ResetPasswordPage() {
  const { token } = useParams()
  const nav = useNavigate()

  const [p1, setP1] = useState("")
  const [p2, setP2] = useState("")
  const [err, setErr] = useState("")
  const [ok, setOk] = useState(false)
  const [showP1, setShowP1] = useState(false)
  const [showP2, setShowP2] = useState(false)
  const [saving, setSaving] = useState(false)

  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/

  const submit = async e => {
    e.preventDefault()
    if (saving) return
    setErr("")

    if (p1 !== p2) { setErr("รหัสผ่านไม่ตรงกัน"); return }
    if (!passwordRegex.test(p1)) {
      setErr("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร ประกอบด้วยตัวอักษรและตัวเลข")
      return
    }

    setSaving(true)
    try {
      await resetPassword(token, p1)
      setOk(true)
      setTimeout(() => nav("/login", { replace: true }), 1800)
    } catch {
      setErr("ลิงก์หมดอายุหรือไม่ถูกต้อง")
    } finally {
      setSaving(false)
    }
  }

  const fieldCls = "w-full border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white/70"

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="w-full max-w-md bg-white/80 backdrop-blur-xl rounded-2xl px-8 py-10 shadow-2xl flex flex-col gap-6"
      >
        {ok ? (
          <>
            <h1 className="text-3xl font-extrabold text-center text-primary">ตั้งรหัสผ่านสำเร็จ</h1>
            <p className="text-center text-gray-600 -mt-2">กำลังนำไปหน้าเข้าสู่ระบบ…</p>
          </>
        ) : (
          <>
            <div className="space-y-1 text-center">
              <h1 className="text-3xl font-extrabold text-primary"><img src="/images/app-logo.png"  className="h-20 mx-auto opacity-95 mb-5" /></h1>
      
               <h1 className="text-2xl font-extrabold text-gray-900">ตั้งรหัสผ่านใหม่</h1>
              <p className="text-gray-500 text-sm">โปรดตั้งรหัสผ่านใหม่เพื่อความปลอดภัย</p>
            </div>
            {err && <p className="text-center text-danger text-sm -mt-2">{err}</p>}

            <form onSubmit={submit} className="flex flex-col gap-4">
              <Field>
                <input
                  type={showP1 ? "text" : "password"}
                  placeholder="Password"
                  value={p1}
                  onChange={e => setP1(e.target.value)}
                  disabled={saving}
                  className={fieldCls}
                />
                {showP1 ? (
                  <FaEyeSlash
                    className="absolute right-4 text-gray-500 cursor-pointer"
                    onClick={() => setShowP1(false)}
                  />
                ) : (
                  <FaEye
                    className="absolute right-4 text-gray-500 cursor-pointer"
                    onClick={() => setShowP1(true)}
                  />
                )}
              </Field>

              <Field>
                <input
                  type={showP2 ? "text" : "password"}
                  placeholder="Confirm password"
                  value={p2}
                  onChange={e => setP2(e.target.value)}
                  disabled={saving}
                  className={fieldCls}
                />
                {showP2 ? (
                  <FaEyeSlash
                    className="absolute right-4 text-gray-500 cursor-pointer"
                    onClick={() => setShowP2(false)}
                  />
                ) : (
                  <FaEye
                    className="absolute right-4 text-gray-500 cursor-pointer"
                    onClick={() => setShowP2(true)}
                  />
                )}
              </Field>

              <button
                disabled={saving}
                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 transition text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 shadow-lg"
              >
                {saving && <Loader2 className="h-5 w-5 animate-spin" />}
                {saving ? "กำลังบันทึก…" : "บันทึกรหัสผ่านใหม่"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </section>
  )
}
